/**
 * The door for a build with an identity provider.
 *
 * Same cover as the in-browser gate, a different lock: the password is
 * checked by Google Identity Platform, not by this browser, and what comes
 * back is a token the platform verifies for itself. There is no enrolment
 * here — accounts are created by an administrator, and a person sets their
 * first password through the reset email. A sign-up form on a public site
 * would let anybody on the internet make themselves a principal.
 */
import { useEffect, useRef, useState } from "react";
import { GateFrame } from "./LoginGate";
import { SignInRefused, sendPasswordReset, signInToPlatform, type PlatformUser } from "../auth/platformIdentity";
import { t } from "../../i18n";

type Stage = "credentials" | "forgot" | "sent";

export interface PlatformGateProps {
  peek?: boolean;
  onEnter: (user: PlatformUser) => void;
  onBack?: () => void;
  onSignOut?: () => void;
  /** why the person is looking at the door again, e.g. signed out for inactivity */
  notice?: string;
}

export function PlatformGate({ peek = false, onEnter, onBack, onSignOut, notice = "" }: PlatformGateProps) {
  const [stage, setStage] = useState<Stage>("credentials");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [msg, setMsg] = useState(notice);
  const [busy, setBusy] = useState(false);

  const focusRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => focusRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [stage]);

  const run = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      await work();
    } catch (err) {
      setMsg(err instanceof SignInRefused ? t(err.message) : err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const doSignIn = () =>
    run(async () => {
      const em = email.trim();
      if (!em || !pass) throw new SignInRefused("Both fields are needed.");
      onEnter(await signInToPlatform(em, pass));
    });

  const doForgot = () =>
    run(async () => {
      const em = email.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) throw new SignInRefused("That does not look like an email address.");
      await sendPasswordReset(em);
      setStage("sent");
    });

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    if (stage === "credentials") void doSignIn();
    else if (stage === "forgot") void doForgot();
  };

  return (
    <GateFrame>
      {peek ? (
        <div className="gcard" style={{ borderTopColor: "#3FD07A" }}>
          <div className="eyebrow">{t("You are already in")}</div>
          <h2>{t("Nothing has stopped")}</h2>
          <p>{t("This is the cover, shown while your session keeps running behind it.")}</p>
          <button className="btn primary" onClick={() => onBack?.()}>
            {t("BACK TO THE DASHBOARD")}
          </button>
          <button className="btn ghost" onClick={() => onSignOut?.()}>
            {t("SIGN OUT INSTEAD")}
          </button>
        </div>
      ) : stage === "credentials" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Sign in")}</div>
          <h2>{t("Sign in to the platform")}</h2>
          <p>{t("Use the account your administrator created for you.")}</p>
          <label>
            {t("Email address")}
            <input
              type="email"
              ref={focusRef}
              autoComplete="username"
              placeholder="you@tibah.example"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          <label>
            {t("Password")}
            <input
              type="password"
              autoComplete="current-password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" disabled={busy} onClick={() => void doSignIn()}>
            {busy ? t("SIGNING IN…") : t("SIGN IN")}
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setMsg("");
              setStage("forgot");
            }}
          >
            {t("FIRST TIME, OR FORGOTTEN THE PASSWORD?")}
          </button>
        </div>
      ) : stage === "forgot" ? (
        <div className="gcard">
          <div className="eyebrow">{t("Set a password")}</div>
          <h2>{t("We will email you a link")}</h2>
          <p>
            {t(
              "Enter the address your account was created with. The link lets you choose a new password, or your first one.",
            )}
          </p>
          <label>
            {t("Email address")}
            <input
              type="email"
              ref={focusRef}
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKey}
            />
          </label>
          {msg && <div className="gmsg">{msg}</div>}
          <button className="btn primary" disabled={busy} onClick={() => void doForgot()}>
            {t("SEND THE LINK")}
          </button>
          <button className="btn ghost" onClick={() => setStage("credentials")}>
            {t("BACK TO SIGN IN")}
          </button>
        </div>
      ) : (
        <div className="gcard">
          <div className="eyebrow">{t("Check your inbox")}</div>
          <h2>{t("If that address has an account, the link is on its way")}</h2>
          <p>
            {t(
              "It can take a minute, and may land in junk mail. This screen does not say whether the address has an account, so that nobody can use it to find out.",
            )}
          </p>
          <button className="btn primary" onClick={() => setStage("credentials")}>
            {t("BACK TO SIGN IN")}
          </button>
        </div>
      )}
    </GateFrame>
  );
}
